import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  setOfferCombinationError,
  setOfferSelectedGuarantees,
  setShownProductsIds,
} from '../../store/redux/slices/offersSlice';
import {
  isFailedQuote,
  isOfferSuccessfull,
  LouiseQuote,
  ProductWithQuality,
} from '../../../../../types/Quotes';
import { RootState } from '../../store/redux/reducers/rootReducer';
import SingleOfferContainer from './SingleOfferContainer';
import SingleOfferContainerMobile from './SingleOfferContainerMobile';
import { useQuote } from '../../hooks/useQuoteQueries';
import { useBoolean, useBreakpointValue } from '@wegroup/design-system';
import OfferReportModal from './OfferReportModal';
import { sha256 } from '../../../../common/src/utils/commonUtils';
import {
  getOfferForProductAndQuote,
  OfferState,
} from '../../services/offersService';
import SingleOfferCarousel from './SingleOfferCarousel';
import { getGuaranteeNameForKey } from '../../services/productFactoryService';
import { isSocialLoginUrl } from '../../../../app/src/features/authentication/constants/SocialAuth';

interface Props {
  product: ProductWithQuality;
  riskObject: string;
  addToSelectedOffers: (arg0: string) => void;
  removeFromSelectedOffers: (id?: string) => void;
}

const SingleOffer: React.FC<React.PropsWithChildren<Props>> = ({
  product,
  riskObject,
  addToSelectedOffers,
  removeFromSelectedOffers,
}) => {
  const dispatch = useDispatch();

  const [blur, setBlur] = useBoolean();
  const [showErrorModal, setShowErrorModal] = useBoolean();
  // Report modal only accepts setState
  const [showReportModal, setShowReportModal] = useState(false);

  const hasLoadedOnce = useRef<boolean>(false);

  const isMobile = useBreakpointValue({ base: true, laptop: false });

  const selectedOffers = useSelector(
    (state: RootState) => state.offers.selectedOffers,
  );
  const quoteIdToUrl = useSelector(
    (state: RootState) => state.offers.quoteIdToUrl,
  );
  const shownProductIds = useSelector(
    (state: RootState) => state.offers.shownProductIds,
  );
  const activeCarQuoteFilter = useSelector(
    (state: RootState) => state.offers.activeCarQuoteFilter,
  );

  const { data, refetch, isFetching } = useQuote(
    riskObject,
    product.hash_id,
    activeCarQuoteFilter,
  );
  const quote = data as LouiseQuote | undefined;

  const offer = getOfferForProductAndQuote(product, quote);

  const isComparing = false;
  const isSelected = Boolean(
    offer.hash_id && selectedOffers[riskObject]?.includes(offer.hash_id),
  );

  const handleSelect = (id?: string) => {
    if (!id) return;
    if (isSelected) removeFromSelectedOffers(id);
    else addToSelectedOffers(id);
  };

  const refetchQuote = async () => {
    const { data: quote } = await refetch();
    if (isFailedQuote(quote)) setShowErrorModal.on();
  };

  const pageId = ['car', 'two_wheeler'].includes(riskObject)
    ? activeCarQuoteFilter
    : riskObject;

  const onOptionsChange = async (guarantees: string[]) => {
    const targetProductHash = await sha256(
      offer.info.company.key +
        offer.info.insurance_type +
        guarantees.sort().join('') +
        (product.risk_object_revision_id || 'None'),
    );

    let hashWithoutNature = targetProductHash;

    if (guarantees.includes('nature')) {
      hashWithoutNature = await sha256(
        offer.info.company.key +
          offer.info.insurance_type +
          guarantees
            .filter((name) => name !== 'nature')
            .sort()
            .join(''),
      );
    }

    if (quoteIdToUrl[targetProductHash] || quoteIdToUrl[hashWithoutNature]) {
      const currentHashIdIndex = shownProductIds[pageId].findIndex(
        (hashId) => hashId === offer.hash_id,
      );
      const newShownProductIds = [...shownProductIds[pageId]];
      newShownProductIds.splice(
        currentHashIdIndex,
        1,
        quoteIdToUrl[targetProductHash] ? targetProductHash : hashWithoutNature,
      );

      // Replace the current product id with another

      dispatch(
        setOfferCombinationError({ quoteId: offer.hash_id, value: false }),
      );
      dispatch(
        setShownProductsIds({
          pageId,
          productIds: newShownProductIds,
        }),
      );
    } else {
      dispatch(
        setOfferCombinationError({ quoteId: offer.hash_id, value: true }),
      );
    }

    dispatch(
      setOfferSelectedGuarantees({
        riskObject: offer.info.insurance_type,
        company: offer.info.company.key,
        value: guarantees,
      }),
    );
  };

  const triggerBlur = () => {
    setBlur.on();
    setTimeout(setBlur.off, 500);
  };

  const getNextOmniumGuarantee = (direction: 'LEFT' | 'RIGHT') => {
    const oldGuarantee = offer.info.insurance_name;
    const guaranteeIndex = offer.possible_insurances?.indexOf(
      offer.info.insurance_name,
    );

    if (guaranteeIndex != null && offer.possible_insurances) {
      let newGuaranteeIndex;
      if (guaranteeIndex === 0 && direction === 'LEFT') {
        newGuaranteeIndex = offer.possible_insurances.length - 1;
      } else if (
        guaranteeIndex === offer.possible_insurances.length - 1 &&
        direction === 'RIGHT'
      ) {
        newGuaranteeIndex = 0;
      } else {
        newGuaranteeIndex =
          direction === 'LEFT' ? guaranteeIndex - 1 : guaranteeIndex + 1;
      }

      const newGuarantee = offer.possible_insurances[newGuaranteeIndex];
      return [oldGuarantee, newGuarantee];
    }

    return [oldGuarantee || '', ''];
  };

  const changeOmniumGuarantee = (direction: 'LEFT' | 'RIGHT') => {
    const [oldGuarantee, newGuarantee] = getNextOmniumGuarantee(direction);

    if (oldGuarantee && newGuarantee) {
      const newGuarantees = [
        ...offer.selected_guarantees?.filter(
          (_guarantee) => _guarantee !== oldGuarantee,
        ),
        newGuarantee,
      ];

      onOptionsChange(newGuarantees);
    }
  };

  // Used for carousel
  const handleOnLeftClick = () => {
    triggerBlur();
    changeOmniumGuarantee('LEFT');
  };

  const handleOnRightClick = () => {
    triggerBlur();
    changeOmniumGuarantee('RIGHT');
  };

  useEffect(() => {
    if (isOfferSuccessfull(offer)) {
      setShowErrorModal.off();
    }
  }, []);

  if (
    isSocialLoginUrl &&
    !hasLoadedOnce.current &&
    (offer.state === OfferState.ERROR || offer.state === OfferState.LOADING)
  ) {
    return null;
  } else if (!hasLoadedOnce.current) {
    hasLoadedOnce.current = true;
  }

  return (
    <SingleOfferCarousel
      onLeftClick={handleOnLeftClick}
      onRightClick={handleOnRightClick}
      leftHoverText={getGuaranteeNameForKey(
        getNextOmniumGuarantee('LEFT')[1],
        offer.guarantee_content,
      )}
      rightHoverText={getGuaranteeNameForKey(
        getNextOmniumGuarantee('RIGHT')[1],
        offer.guarantee_content,
      )}
      buttonsDisabled={isFetching}
      enabled={(offer.possible_insurances?.length || 0) > 1}
      showBlur={blur}
    >
      <OfferReportModal
        showReportModal={showReportModal}
        setShowReportModal={setShowReportModal}
        offer={offer}
      />
      {isMobile ? (
        <SingleOfferContainerMobile
          offer={offer}
          isComparing={isComparing}
          isSelected={selectedOffers[riskObject]?.includes(offer.hash_id)}
          onAddToSelected={() => handleSelect(offer.hash_id)}
          onOptionsChange={onOptionsChange}
          refetch={refetchQuote}
        />
      ) : (
        <SingleOfferContainer
          showErrorModal={showErrorModal}
          closeErrorModal={setShowErrorModal.off}
          offer={offer}
          isComparing={isComparing}
          isSelected={selectedOffers[riskObject]?.includes(offer.hash_id)}
          onAddToSelected={() => handleSelect(offer.hash_id)}
          refetch={refetchQuote}
          onReport={() => setShowReportModal(true)}
          onOptionsChange={onOptionsChange}
        />
      )}
    </SingleOfferCarousel>
  );
};

export default SingleOffer;
